/**
 * DD/MM/YYYY typed entry helpers (digits-only, slash-separated display).
 */

export type DmyDateMode = 'birth' | 'calendar';

export interface DmyParts {
  day: number;
  month: number;
  year: number;
}

export function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/** Formats up to 8 digits as DD/MM/YYYY with slashes. */
export function formatDmyFromDigits(digits: string): string {
  const d = digitsOnly(digits).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export function parseDmyPartsFromDisplay(display: string): DmyParts | null {
  const dig = digitsOnly(display);
  if (dig.length !== 8) return null;
  const day = parseInt(dig.slice(0, 2), 10);
  const month = parseInt(dig.slice(2, 4), 10);
  const year = parseInt(dig.slice(4, 8), 10);
  return { day, month, year };
}

/** Whole calendar days from today. `0` is today, `-1` is yesterday. */
export function calendarDayOffset(date: Date, today = new Date()): number {
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((startOfDate.getTime() - startOfToday.getTime()) / 86400000);
}

export function isValidCalendarDate(day: number, month: number, year: number): boolean {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
  if (month < 1 || month > 12) return false;
  if (year < 1 || year > 9999) return false;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

const CALENDAR_MIN_YEAR = 1900;
const CALENDAR_MAX_YEAR = 2100;

function startOfTodayLocal(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Birth: valid calendar date, year ≤ current calendar year, not after today (local). */
export function isValidBirthDate(parts: DmyParts, now: Date = new Date()): boolean {
  if (!isValidCalendarDate(parts.day, parts.month, parts.year)) return false;
  if (parts.year > now.getFullYear()) return false;
  const d = new Date(parts.year, parts.month - 1, parts.day);
  return d <= startOfTodayLocal(now);
}

/** Calendar (schedule): valid date; year in [1900, 2100]; allows future days. */
export function isValidCalendarModeDate(parts: DmyParts): boolean {
  if (!isValidCalendarDate(parts.day, parts.month, parts.year)) return false;
  if (parts.year < CALENDAR_MIN_YEAR || parts.year > CALENDAR_MAX_YEAR) return false;
  return true;
}

export function validateDmyParts(mode: DmyDateMode, parts: DmyParts, now: Date = new Date()): boolean {
  return mode === 'birth' ? isValidBirthDate(parts, now) : isValidCalendarModeDate(parts);
}

/** Local calendar date at midnight from parts. */
export function localDateFromDmyParts(parts: DmyParts): Date {
  return new Date(parts.year, parts.month - 1, parts.day);
}

export function dateToDateOnlyLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format a local Date as DD/MM/YYYY using local calendar components. */
export function formatDmyDate(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/** ISO datetime at UTC midnight derived from local Y/M/D, e.g. 1980-06-15T00:00:00Z. */
export function dateToUtcMidnightIso(d: Date): string {
  return `${dateToDateOnlyLocal(d)}T00:00:00Z`;
}

/** Normalize API / legacy values to YYYY-MM-DD using local calendar components. */
export function normalizeSubjectDobToDateOnly(value: string | null | undefined): string | null {
  if (value == null || String(value).trim() === '') return null;
  const trimmed = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return null;
  return dateToDateOnlyLocal(d);
}

export function dateOnlyStringToLocalDate(ymd: string): Date | null {
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (!isValidCalendarDate(d, mo, y)) return null;
  return new Date(y, mo - 1, d);
}
